import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  MapPin, Image as ImageIcon, Video, Lightbulb, Plus, Trash2, Save, 
  ExternalLink, Eye, X, Ship, Map, Compass, PartyPopper
} from 'lucide-react';

const CITIES = [
  { id: 'playa_del_carmen', name: 'Playa del Carmen' },
  { id: 'cancun', name: 'Cancún' },
  { id: 'tulum', name: 'Tulum' },
  { id: 'los_cabos', name: 'Los Cabos' }
];

const ACTIVITY_CATEGORIES = [
  { id: 'yacht', name: 'Yacht', icon: Ship },
  { id: 'tour', name: 'Tour', icon: Map },
  { id: 'activity', name: 'Activity', icon: Compass },
  { id: 'party', name: 'Party', icon: PartyPopper }
];

export default function AdminCityContent() {
  const { api } = useAuth();
  const { language } = useLanguage();
  
  const [selectedCity, setSelectedCity] = useState('playa_del_carmen');
  const [content, setContent] = useState({
    cover_image: '',
    gallery_images: [],
    videos: [],
    description_en: '',
    description_es: '',
    tips_en: [],
    tips_es: [],
    activities: []
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Temp inputs for adding items
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [newVideo, setNewVideo] = useState({ url: '', title: '', type: 'youtube' });
  const [newTipEn, setNewTipEn] = useState('');
  const [newTipEs, setNewTipEs] = useState('');
  const [newActivity, setNewActivity] = useState({
    title_en: '',
    title_es: '',
    video_url: '',
    image_url: '',
    category: 'activity',
    description_en: '',
    description_es: ''
  });

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get(`/city-content/${selectedCity}`);
      setContent({
        cover_image: res.data.cover_image || '',
        gallery_images: res.data.gallery_images || [],
        videos: res.data.videos || [],
        description_en: res.data.description_en || '',
        description_es: res.data.description_es || '',
        tips_en: res.data.tips_en || [],
        tips_es: res.data.tips_es || [],
        activities: res.data.activities || []
      });
    } catch (error) {
      console.error('Error fetching city content:', error);
    } finally {
      setLoading(false);
    }
  }, [api, selectedCity]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api().post('/admin/city-content', {
        city_id: selectedCity,
        ...content
      });
      toast.success(language === 'en' ? 'Content saved successfully' : 'Contenido guardado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving content');
    } finally {
      setSaving(false);
    }
  };

  // Gallery handlers
  const addGalleryImage = () => {
    if (newGalleryUrl.trim()) {
      setContent(prev => ({
        ...prev,
        gallery_images: [...prev.gallery_images, newGalleryUrl.trim()]
      }));
      setNewGalleryUrl('');
    }
  };

  const removeGalleryImage = (index) => {
    setContent(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  // Video handlers
  const addVideo = () => {
    if (newVideo.url.trim()) {
      setContent(prev => ({
        ...prev,
        videos: [...prev.videos, { ...newVideo }]
      }));
      setNewVideo({ url: '', title: '', type: 'youtube' });
    }
  };

  const removeVideo = (index) => {
    setContent(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  // Tips handlers
  const addTipEn = () => {
    if (newTipEn.trim()) {
      setContent(prev => ({
        ...prev,
        tips_en: [...prev.tips_en, newTipEn.trim()]
      }));
      setNewTipEn('');
    }
  };

  const addTipEs = () => {
    if (newTipEs.trim()) {
      setContent(prev => ({
        ...prev,
        tips_es: [...prev.tips_es, newTipEs.trim()]
      }));
      setNewTipEs('');
    }
  };

  const removeTip = (lang, index) => {
    const key = `tips_${lang}`;
    setContent(prev => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index)
    }));
  };

  // Activity handlers
  const addActivity = () => {
    if (newActivity.title_en.trim() || newActivity.title_es.trim()) {
      setContent(prev => ({
        ...prev,
        activities: [...prev.activities, { ...newActivity }]
      }));
      setNewActivity({
        title_en: '',
        title_es: '',
        video_url: '',
        image_url: '',
        category: 'activity',
        description_en: '',
        description_es: ''
      });
    }
  };

  const removeActivity = (index) => {
    setContent(prev => ({
      ...prev,
      activities: prev.activities.filter((_, i) => i !== index)
    }));
  };

  const cityName = CITIES.find(c => c.id === selectedCity)?.name || selectedCity;

  return (
    <div className="space-y-6" data-testid="admin-city-content">
      {/* City Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={selectedCity} onValueChange={setSelectedCity}>
            <SelectTrigger className="w-60">
              <MapPin size={16} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CITIES.map(city => (
                <SelectItem key={city.id} value={city.id}>
                  {city.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <a 
            href={`/city/${selectedCity}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-caribbean hover:underline flex items-center gap-1 text-sm"
          >
            <Eye size={16} />
            {language === 'en' ? 'Preview' : 'Vista previa'}
          </a>
        </div>
        
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-caribbean hover:bg-caribbean/90"
        >
          <Save size={16} className="mr-2" />
          {saving 
            ? (language === 'en' ? 'Saving...' : 'Guardando...') 
            : (language === 'en' ? 'Save All Changes' : 'Guardar Cambios')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-caribbean"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cover Image */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ImageIcon size={20} className="text-caribbean" />
                {language === 'en' ? 'Cover Image' : 'Imagen de Portada'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="https://example.com/image.jpg"
                value={content.cover_image}
                onChange={(e) => setContent(prev => ({ ...prev, cover_image: e.target.value }))}
              />
              {content.cover_image && (
                <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                  <img 
                    src={content.cover_image} 
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin size={20} className="text-caribbean" />
                {language === 'en' ? 'Description' : 'Descripción'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">English</label>
                <Textarea
                  placeholder="Description in English..."
                  value={content.description_en}
                  onChange={(e) => setContent(prev => ({ ...prev, description_en: e.target.value }))}
                  rows={3}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Español</label>
                <Textarea
                  placeholder="Descripción en Español..."
                  value={content.description_es}
                  onChange={(e) => setContent(prev => ({ ...prev, description_es: e.target.value }))}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Gallery Images */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ImageIcon size={20} className="text-rosa" />
                {language === 'en' ? 'Gallery' : 'Galería'}
                <Badge variant="secondary">{content.gallery_images.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/photo.jpg"
                  value={newGalleryUrl}
                  onChange={(e) => setNewGalleryUrl(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addGalleryImage()}
                />
                <Button onClick={addGalleryImage} size="icon" variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                {content.gallery_images.map((img, index) => (
                  <div key={index} className="relative group aspect-square">
                    <img 
                      src={img} 
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => e.target.src = 'https://via.placeholder.com/150?text=Error'}
                    />
                    <button
                      onClick={() => removeGalleryImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Videos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Video size={20} className="text-purple-600" />
                {language === 'en' ? 'Videos' : 'Videos'}
                <Badge variant="secondary">{content.videos.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Input
                  placeholder="YouTube or Vimeo URL"
                  value={newVideo.url}
                  onChange={(e) => setNewVideo(prev => ({ ...prev, url: e.target.value }))}
                />
                <div className="flex gap-2">
                  <Input
                    placeholder={language === 'en' ? 'Video title' : 'Título del video'}
                    value={newVideo.title}
                    onChange={(e) => setNewVideo(prev => ({ ...prev, title: e.target.value }))}
                  />
                  <Select 
                    value={newVideo.type} 
                    onValueChange={(v) => setNewVideo(prev => ({ ...prev, type: v }))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="vimeo">Vimeo</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addVideo} size="icon" variant="outline">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {content.videos.map((video, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{video.type}</Badge>
                      <span className="text-sm truncate max-w-[200px]">{video.title || video.url}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <a href={video.url} target="_blank" rel="noopener noreferrer">
                        <Button size="icon" variant="ghost" className="h-7 w-7">
                          <ExternalLink size={14} />
                        </Button>
                      </a>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-7 w-7 text-red-500"
                        onClick={() => removeVideo(index)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lightbulb size={20} className="text-gold" />
                {language === 'en' ? 'Travel Tips' : 'Tips de Viaje'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* English Tips */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">English Tips</h4>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a tip in English..."
                      value={newTipEn}
                      onChange={(e) => setNewTipEn(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTipEn()}
                    />
                    <Button onClick={addTipEn} size="icon" variant="outline">
                      <Plus size={16} />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {content.tips_en.map((tip, index) => (
                      <div key={index} className="flex items-start justify-between gap-2 p-2 bg-muted rounded-lg text-sm">
                        <span className="flex-1">{tip}</span>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-6 w-6 text-red-500 flex-shrink-0"
                          onClick={() => removeTip('en', index)}
                        >
                          <X size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Spanish Tips */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Tips en Español</h4>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Agregar tip en Español..."
                      value={newTipEs}
                      onChange={(e) => setNewTipEs(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTipEs()}
                    />
                    <Button onClick={addTipEs} size="icon" variant="outline">
                      <Plus size={16} />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {content.tips_es.map((tip, index) => (
                      <div key={index} className="flex items-start justify-between gap-2 p-2 bg-muted rounded-lg text-sm">
                        <span className="flex-1">{tip}</span>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-6 w-6 text-red-500 flex-shrink-0"
                          onClick={() => removeTip('es', index)}
                        >
                          <X size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activities */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Compass size={20} className="text-caribbean" />
                {language === 'en' ? 'Activities & Tours' : 'Actividades y Tours'}
                <Badge variant="secondary">{content.activities.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Activity Form */}
              <div className="p-4 border rounded-lg space-y-3 bg-muted/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    placeholder="Title (English)"
                    value={newActivity.title_en}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, title_en: e.target.value }))}
                  />
                  <Input
                    placeholder="Título (Español)"
                    value={newActivity.title_es}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, title_es: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    placeholder="Image URL"
                    value={newActivity.image_url}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, image_url: e.target.value }))}
                  />
                  <Input
                    placeholder="Video URL (optional)"
                    value={newActivity.video_url}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, video_url: e.target.value }))}
                  />
                  <Select 
                    value={newActivity.category} 
                    onValueChange={(v) => setNewActivity(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVITY_CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={addActivity} variant="outline" className="w-full">
                  <Plus size={16} className="mr-2" />
                  {language === 'en' ? 'Add Activity' : 'Agregar Actividad'}
                </Button>
              </div>

              {/* Activities List */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {content.activities.map((activity, index) => {
                  const CategoryIcon = ACTIVITY_CATEGORIES.find(c => c.id === activity.category)?.icon || Compass;
                  return (
                    <div key={index} className="border rounded-lg overflow-hidden group relative">
                      {activity.image_url && (
                        <div className="aspect-video bg-muted">
                          <img 
                            src={activity.image_url} 
                            alt={activity.title_en || activity.title_es}
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image'}
                          />
                        </div>
                      )}
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <CategoryIcon size={14} className="text-caribbean" />
                          <Badge variant="outline" className="text-xs">{activity.category}</Badge>
                        </div>
                        <p className="font-medium text-sm">{activity.title_en}</p>
                        <p className="text-xs text-muted-foreground">{activity.title_es}</p>
                      </div>
                      <button
                        onClick={() => removeActivity(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
